import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, CalendarClock, CheckCircle2, ChevronLeft, ChevronRight,
  Clock3, FileClock, Flag, GitFork, History, LoaderCircle, Milestone, Printer,
  MoreHorizontal, RefreshCcw, RotateCcw, Save, Search, Send, ShieldAlert, StickyNote, Trash2, XCircle,
} from "lucide-react";
import {
  Alert as MuiAlert,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  createLookAheadDraft, downloadLookAheadPlanPdf, downloadLookAheadPlan, getCurrentLookAheadPlan, getLookAheadDraft,
  getLookAheadError, getLookAheadHistory, getLookAheadPlan,
  publishLookAheadDraft, regenerateLookAheadDraft, updateLookAheadAction,
  updateLookAheadNotes, validateLookAheadDraft,
} from "../services/lookAheadApi";
import "./LookAhead.css";
import "../Components/jeconDialog.css";

const PAGE_SIZE = 20;

export const mergeLookAheadActionDelta = (currentPlan, delta) => {
  if (!currentPlan || !delta || delta.id !== currentPlan.id) return currentPlan;
  const changedActions = new Map((delta.actions || []).map((action) => [action.id, action]));
  const has = (key) => Object.prototype.hasOwnProperty.call(delta, key);
  return {
    ...currentPlan,
    ...(has("version") ? { version: delta.version } : {}),
    ...(has("validation_status") ? { validation_status: delta.validation_status } : {}),
    ...(has("validation") ? { validation: delta.validation } : {}),
    ...(has("data_issues") ? { data_issues: delta.data_issues } : {}),
    ...(has("updated_by") ? { updated_by: delta.updated_by } : {}),
    ...(has("updated_at") ? { updated_at: delta.updated_at } : {}),
    actions: changedActions.size
      ? currentPlan.actions.map((action) => changedActions.get(action.id) || action)
      : currentPlan.actions,
  };
};

export const readSelectedProjectId = () => {
  try {
    const value = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(value?.project_id || value?.id || value || "").trim();
  } catch { return ""; }
};

const roleMayPrepare = () => {
  const role = (localStorage.getItem("role_name") || "").trim().toLowerCase();
  return ["admin", "administrator", "field technician"].includes(role);
};

export const formatPlanDateTime = (value, timezone) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit",
      minute: "2-digit", hour12: true, timeZone: timezone || undefined,
    }).format(date);
  } catch { return date.toLocaleString("en-IN"); }
};

const formatBoxWindow = (box, timezone) => {
  const start = new Date(box.start);
  const end = new Date(box.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "Planning window";
  try {
    const date = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", timeZone: timezone }).format(start);
    const startTime = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone }).format(start);
    const endDate = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", timeZone: timezone }).format(end);
    const endTime = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone }).format(end);
    return `${date} ${startTime} → ${endDate} ${endTime}`;
  } catch { return `${formatPlanDateTime(box.start, timezone)} → ${formatPlanDateTime(box.end, timezone)}`; }
};
const formatShortPlanDateTime = (value, timezone) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone || undefined }).format(date).replace(",", "");
  } catch { return formatPlanDateTime(value, timezone); }
};

const label = (action) => action.source_type === "MILESTONE"
  ? action.process_code || action.process_name || "Milestone"
  : action.equipment_number || action.process_code || "Equipment process";

const SUCCESSOR_EVENT_BY_RELATION = {
  FS: "START",
  SS: "START",
  FF: "END",
  SF: "END",
};
const DEPENDENCY_RELATION_LABELS = {
  FS: "Finish-to-Start",
  SS: "Start-to-Start",
  FF: "Finish-to-Finish",
  SF: "Start-to-Finish",
};

const formatLagUnit = (unit) => {
  const normalized = String(unit || "").trim().toLowerCase();
  if (["minute", "minutes", "min", "mins", "m"].includes(normalized)) return "mins";
  if (["hour", "hours", "hr", "hrs", "h"].includes(normalized)) return "hrs";
  if (["day", "days", "d"].includes(normalized)) return "days";
  return normalized;
};

export const formatDependencyCode = (dependency) => {
  const predecessor = dependency?.predecessor || {};
  const code = predecessor.process_code || predecessor.equipment_number
    || dependency?.depends_on_process_id;
  if (!code) return "";

  const relation = String(dependency?.relation || "FS").trim().toUpperCase();
  const rawLag = dependency?.lag_value;
  const lag = rawLag === null || rawLag === undefined || rawLag === ""
    ? null
    : Number(rawLag);
  if (lag === null || !Number.isFinite(lag) || lag === 0) return `${code}${relation}`;

  const unit = formatLagUnit(dependency?.lag_unit);
  const sign = lag > 0 ? "+" : "-";
  return `${code}${relation}${sign}${Math.abs(lag)}${unit}`;
};

export const formatDuration = (hours) => {
  const numericHours = Number(hours);
  if (!Number.isFinite(numericHours) || numericHours < 0) return "";
  const totalMinutes = Math.round(numericHours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!wholeHours) return `${minutes} ${minutes === 1 ? "min" : "mins"}`;
  if (!minutes) return `${wholeHours} ${wholeHours === 1 ? "hr" : "hrs"}`;
  return `${wholeHours} ${wholeHours === 1 ? "hr" : "hrs"} ${minutes} ${minutes === 1 ? "min" : "mins"}`;
};

const dependencyCodes = (action) => (action.dependencies || [])
  .filter((dependency) => {
    const relation = String(dependency?.relation || "FS").trim().toUpperCase();
    return SUCCESSOR_EVENT_BY_RELATION[relation] === action.action_type;
  })
  .map(formatDependencyCode)
  .filter(Boolean);

const searchText = (action) => [
  action.equipment_number, action.equipment_group, action.process_code,
  action.process_name, action.action_type,
  ...dependencyCodes(action),
  ...(action.responsible_departments || []).map((item) => item.department_name),
].filter(Boolean).join(" ").toLowerCase();

// A process has two planning events.  Keep their natural execution order when
// both are displayed together, regardless of the database's row/UUID order.
export const compareCandidateActions = (left, right, sortBy) => {
  const primaryComparison = sortBy === "process_code"
    ? String(left.process_code || "").localeCompare(String(right.process_code || ""), undefined, { numeric: true, sensitivity: "base" })
    : String(left.equipment_number || "").localeCompare(String(right.equipment_number || ""), undefined, { numeric: true, sensitivity: "base" });
  if (primaryComparison !== 0) return primaryComparison;

  if (String(left.equipment_process_id || "") !== String(right.equipment_process_id || "")) return 0;
  return (left.action_type === "START" ? 0 : 1) - (right.action_type === "START" ? 0 : 1);
};

const isUpcomingDependency = (action) => action.action_type === "START"
  && !action.selectable
  && (action.errors || []).some((issue) => ["NO_DEPENDENCY_ANCHOR", "BOX_NOT_FEASIBLE"].includes(issue.code));

const lagMilliseconds = (value, unit) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return null;
  const normalized = String(unit || "").trim().toLowerCase();
  if (["minute", "minutes", "min", "mins", "m"].includes(normalized)) return amount * 60_000;
  if (["hour", "hours", "hr", "hrs", "h"].includes(normalized)) return amount * 3_600_000;
  if (["day", "days", "d"].includes(normalized)) return amount * 86_400_000;
  return amount === 0 ? 0 : null;
};

export const earliestEligibleTime = (action) => {
  const evidenceById = new Map((action.dependency_evidence || []).map((item) => [item.dependency_id, item]));
  const eligibleTimes = (action.dependencies || []).flatMap((dependency) => {
    const evidence = evidenceById.get(dependency.dependency_id);
    const timestamp = new Date(evidence?.actual_timestamp);
    const lag = lagMilliseconds(dependency.lag_value, dependency.lag_unit);
    if (!evidence?.actual_timestamp || Number.isNaN(timestamp.getTime()) || lag === null) return [];
    return [timestamp.getTime() + lag];
  });
  return eligibleTimes.length ? new Date(Math.max(...eligibleTimes)).toISOString() : null;
};

const Dependencies = ({ action, onDependencyClick }) => {
  const dependencies = constrainedDependencies(action);
  if (!dependencies.length) return null;
  return <div className="la-dependencies">
    <span>Dependency:</span>
    {dependencies.map((dependency, index) => {
      const code = formatDependencyCode(dependency);
      const relation = String(dependency.relation || "FS").toUpperCase();
      const lag = dependency.lag_value === null || dependency.lag_value === undefined || dependency.lag_value === "" ? "" : `${Number(dependency.lag_value) >= 0 ? "+" : ""}${dependency.lag_value} ${formatLagUnit(dependency.lag_unit)} lag`;
      const explanation = `${DEPENDENCY_RELATION_LABELS[relation] || relation}${lag ? `, ${lag}` : ""}`;
      const chip = onDependencyClick ? <button type="button" onClick={onDependencyClick} aria-label={`View dependency path for ${code}`}>{code}</button> : <strong>{code}</strong>;
      return <Tooltip key={`${code}-${index}`} title={explanation}><span>{chip}</span></Tooltip>;
    })}
  </div>;
};

const constrainedDependencies = (action) => (action.dependencies || []).filter((dependency) => {
  const relation = String(dependency?.relation || "FS").trim().toUpperCase();
  return SUCCESSOR_EVENT_BY_RELATION[relation] === action.action_type;
});

const dependencyEventLabel = (dependency) => `${dependency?.predecessor?.process_code
  || dependency?.predecessor?.equipment_number || "Unknown predecessor"} ${dependency?.required_predecessor_event || "event"}`;

const EndDuration = ({ action, always = false }) => {
  if (!always && action.action_type !== "END") return null;
  const duration = formatDuration(action.duration_hours);
  if (!duration) return null;
  return <small className="la-end-duration"><Clock3 size={12} /> Expected END {duration} after START</small>;
};

const ActionTiming = ({ action, timezone, compact = false }) => {
  const isEnd = action.action_type === "END";
  const eta = isEnd ? action.finish_eta : action.start_eta;
  if (!eta && !(isEnd && action.actual_start)) return null;

  return <div className="la-action-timing">
    {isEnd && action.actual_start && <small className="actual">
      <Clock3 size={12} /> {compact ? `Actual start ${formatShortPlanDateTime(action.actual_start, timezone)}` : `Actual START: ${formatPlanDateTime(action.actual_start, timezone)}`}
    </small>}
    {eta && <small>
      <CalendarClock size={12} /> {isEnd ? "End ETA" : "Start ETA"}: {compact ? formatShortPlanDateTime(eta, timezone) : formatPlanDateTime(eta, timezone)}
    </small>}
  </div>;
};

const Issues = ({ action, includeErrors = true }) => {
  const issues = [
    ...(includeErrors ? action.errors || [] : []).map((item) => ({ ...item, kind: "error" })),
    ...(action.warnings || []).map((item) => ({ ...item, kind: "warning" })),
  ].slice(0, 3);
  return issues.length ? <div className="la-issues">{issues.map((item, index) => (
    <span className={`is-${item.kind}`} key={`${item.code}-${index}`}>
      <AlertCircle size={12} /> {item.message || item.code}
    </span>
  ))}</div> : null;
};

export const dependencyStatus = (dependency, action, actions, timezone) => {
  const evidence = (action.dependency_evidence || []).find((item) => (
    String(item.dependency_id || "") === String(dependency.dependency_id || "")
    || (
      String(item.predecessor_process_id || "") === String(dependency.depends_on_process_id || "")
      && String(item.required_predecessor_event || "").toUpperCase() === String(dependency.required_predecessor_event || "").toUpperCase()
    )
  ));
  if (String(evidence?.anchor_source || "").toUpperCase() === "BYPASS") {
    return {
      label: `Dependency released by bypass${evidence?.bypassed_at || evidence?.actual_timestamp ? ` · ${formatPlanDateTime(evidence.bypassed_at || evidence.actual_timestamp, timezone)}` : ""}`,
      tone: "bypass",
    };
  }
  if (evidence?.actual_timestamp) {
    const event = String(dependency.required_predecessor_event || "event").toUpperCase();
    const state = event === "END" ? "Completed" : "Started";
    return { label: `${state} · actual ${event} ${formatPlanDateTime(evidence.actual_timestamp, timezone)}`, tone: "actual" };
  }
  if (evidence?.current_status) {
    return {
      label: String(evidence.current_status).replace(/_/g, " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase()),
      tone: "blocked",
    };
  }
  const match = (actions || []).find((item) => String(item.equipment_process_id || "") === String(dependency.depends_on_process_id || "")
    && String(item.action_type || "").toUpperCase() === String(dependency.required_predecessor_event || "").toUpperCase());
  if (!match) return { label: "Outside this planning window", tone: "unknown" };
  if (match.actual_timestamp || (match.action_type === "START" && match.actual_start) || (match.action_type === "END" && match.actual_end)) return { label: "Actual recorded", tone: "actual" };
  if (match.decision === "YES") return { label: `Planned in Box ${match.box_number}`, tone: "planned" };
  if (match.selectable) return { label: "Ready to plan", tone: "ready" };
  return { label: "Not planned", tone: "blocked" };
};

const DependencyGraph = ({ action, dependencies, actions, onJump, timezone }) => <div className="la-dependency-graph" aria-label="Dependency path graph">
  <div className="la-dependency-sources">{dependencies.map((dependency, index) => {
    const status = dependencyStatus(dependency, action, actions, timezone);
    const relation = `${String(dependency.relation || "FS").toUpperCase()}${dependency.lag_value ? ` ${Number(dependency.lag_value) > 0 ? "+" : ""}${dependency.lag_value} ${formatLagUnit(dependency.lag_unit)}` : ""}`;
    return <div className="la-dependency-source" key={`${dependency.dependency_id || index}-${dependency.depends_on_process_id}`}>
      <button type="button" className="la-dependency-node" onClick={() => onJump(dependency)}><strong>{dependencyEventLabel(dependency)}</strong><span className={`la-dependency-status ${status.tone}`}>{status.label}</span></button>
      <div className="la-dependency-link"><span>{relation}</span><i aria-hidden="true" /></div>
    </div>;
  })}</div>
  <div className="la-dependency-target"><span className="la-kind">{action.action_type}</span><strong>{label(action)}</strong><small>{action.process_name || "Unnamed process"}</small><em>{action.selectable ? "Dependency relationship" : "Blocked until all inputs are resolved"}</em></div>
</div>;

const MoreInformation = ({ action, timezone, dependencies, blockedReason }) => {
  const isEnd = action.action_type === "END";
  const departments = (action.responsible_departments || []).map((item) => item.department_name).filter(Boolean).join(", ");
  const plannedDuration = formatDuration(action.duration_hours);
  const dependency = dependencies.map(dependencyEventLabel).join(", ");
  const actionLabel = `${action.action_type.slice(0, 1)}${action.action_type.slice(1).toLowerCase()}`;

  const fields = isEnd
    ? [
        ...(blockedReason ? [{ icon: ShieldAlert, label: "Validation", value: blockedReason }] : []),
        ...(dependency ? [{ icon: GitFork, label: "Depends on", value: dependency }] : []),
        ...(departments ? [{ icon: Flag, label: "Department", value: departments }] : []),
        ...(plannedDuration ? [{ icon: Clock3, label: "Planned duration", value: plannedDuration }] : []),
        ...(action.process_code ? [{ icon: FileClock, label: "Process code", value: action.process_code }] : []),
        ...(action.instruction ? [{ icon: StickyNote, label: "Instruction", value: action.instruction }] : []),
      ]
    : [
        ...(departments ? [{ icon: Flag, label: "Department", value: departments }] : []),
        ...(plannedDuration ? [{ icon: Clock3, label: "Planned duration", value: plannedDuration }] : []),
        ...(action.equipment_group ? [{ icon: GitFork, label: "Equipment group", value: action.equipment_group }] : []),
        ...(action.process_code ? [{ icon: FileClock, label: "Process code", value: action.process_code }] : []),
        ...(action.instruction ? [{ icon: StickyNote, label: "Instruction", value: action.instruction }] : []),
      ];

  if (!fields.length) return null;
  return <details className="la-more-information">
    <summary><ChevronRight size={17} aria-hidden="true" /><strong>More information ({actionLabel})</strong></summary>
    <div className="la-more-information-grid">{fields.map(({ icon: Icon, label: fieldLabel, value }) => <div className="la-more-information-item" key={fieldLabel}>
      <Icon size={18} aria-hidden="true" /><div><span>{fieldLabel}</span><strong>{value}</strong></div>
    </div>)}</div>
  </details>;
};

const Candidate = ({ action, timezone, windowStart, windowEnd, showOutOfWindow = true, busy, pendingDecision, onChange, onFocusAction, onJumpToDependency, allActions, compact = false, showCompactDuration = false }) => {
  const selected = action.decision === "YES";
  const enabled = action.selectable || selected;
  const feasibleBoxes = new Set(action.feasible_boxes || []);
  const [pathOpen, setPathOpen] = useState(false);
  const pathDependencies = constrainedDependencies(action);
  const eta = action.action_type === "END" ? action.finish_eta : action.start_eta;
  const outOfWindow = eta && windowStart && windowEnd && (new Date(eta) < new Date(windowStart) || new Date(eta) > new Date(windowEnd));
  const blockedReason = !enabled ? (action.errors || [])[0]?.message || (action.errors || [])[0]?.code || "This action cannot be planned yet." : "";
  const hasWarning = enabled && ((action.warnings || []).length > 0 || String(action.validation_outcome || "").toUpperCase() === "WARNING");
  const warningReason = (action.warnings || [])[0]?.message || "This action has a planning warning.";
  const options = ["NO", "1", "2", "3"];
  const chooseWithKeys = (event) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1;
    let index = options.indexOf(selected ? String(action.box_number) : "NO");
    for (let count = 0; count < options.length; count += 1) {
      index = (index + direction + options.length) % options.length;
      const value = options[index];
      if ((value === "NO" && selected) || (value !== "NO" && enabled && feasibleBoxes.has(Number(value)))) {
        onChange(action, value);
        break;
      }
    }
  };
  return <article id={`la-action-${action.id}`} className={`la-candidate ${selected ? "selected" : ""} ${!action.selectable ? "blocked" : ""}`}>
    <div className="la-candidate-head">
      <div><span className={`la-kind ${action.action_type.toLowerCase()}`}>{action.action_type}</span>{compact && <strong className="la-action-process-name">{action.process_name || action.process_code || "Unnamed process"}</strong>}{compact && eta && <small className="la-row-eta">ETA {formatShortPlanDateTime(eta, timezone)}</small>}{!compact && <strong>{label(action)}</strong>}</div>
      <div className="la-row-status">{!enabled && <span className="la-outcome blocked"><AlertCircle size={12} /> BLOCKED</span>}{hasWarning && <Tooltip title={warningReason}><span className="la-outcome warning"><AlertCircle size={12} /> WARNING</span></Tooltip>}{compact && outOfWindow && showOutOfWindow && <Tooltip title="ETA outside planning window"><span className="la-out-window-icon" aria-label="ETA outside planning window"><Clock3 size={13} /></span></Tooltip>}{selected && <button type="button" className="la-row-box" aria-label={`Planned in Box ${action.box_number}`} onClick={() => onFocusAction(action)}>Box {action.box_number}</button>}</div>
    </div>
    {!compact && <><p>{action.process_name || "Unnamed process"}</p>
      <div className="la-meta">
        {action.process_code && <span>Code: {action.process_code}</span>}
        {action.equipment_group && <span>{action.equipment_group}</span>}
        {!!action.responsible_departments?.length && <span>{action.responsible_departments.map((item) => item.department_name).filter(Boolean).join(", ")}</span>}
      </div></>}
    {compact && <div className="la-candidate-inline">{!!action.responsible_departments?.length && <span>{action.responsible_departments.map((item) => item.department_name).filter(Boolean).join(", ")}</span>}<ActionTiming action={action} timezone={timezone} />{outOfWindow && showOutOfWindow && <Tooltip title={`ETA outside planning window (${formatShortPlanDateTime(windowStart, timezone)} → ${formatShortPlanDateTime(windowEnd, timezone)})`}><span className="la-out-window-icon" aria-label="ETA outside planning window"><Clock3 size={13} /></span></Tooltip>}</div>}
    {action.is_on_hold && <div className="la-hold-state"><strong>On hold</strong>{action.hold_reason && <span>Reason: {action.hold_reason}</span>}</div>}
    {compact ? (pathDependencies.length ? <Dependencies action={action} onDependencyClick={() => setPathOpen(true)} /> : showCompactDuration ? <EndDuration action={action} always /> : <span className="la-row-context-placeholder" aria-hidden="true" />) : <Dependencies action={action} onDependencyClick={() => setPathOpen(true)} />}
    {!compact && <ActionTiming action={action} timezone={timezone} />}
    {!compact && <EndDuration action={action} />}
    {action.source_type === "MILESTONE" && action.downstream_processes?.length > 0 && <small className="la-downstream">Controls {action.downstream_processes.length} downstream process{action.downstream_processes.length === 1 ? "" : "es"}</small>}
    {!compact && <Issues action={action} includeErrors={enabled} />}
    <div className="la-decision" role="group" aria-label={`${label(action)} ${action.action_type} planning decision`} aria-busy={busy || undefined} onKeyDown={chooseWithKeys}>
      <span>Plan in</span>
      <div className="la-decision-options" role="radiogroup" aria-label={`${label(action)} ${action.action_type} options`}>
        <Tooltip title={!selected ? "Already unplanned" : "Unplanned"}><span><button type="button" className={`la-decision-no ${!selected ? "active" : ""} ${busy && pendingDecision === "NO" ? "pending" : ""}`} aria-label={`${label(action)} ${action.action_type} not planned`} aria-pressed={!selected} aria-disabled={busy || !selected} disabled={busy || !selected} onClick={() => onChange(action, "NO")}>
          {busy && pendingDecision === "NO" && <><LoaderCircle size={12} className="spin" aria-hidden="true" /><span className="la-sr-only" role="status" aria-label={`Updating ${label(action)} ${action.action_type}: Not planned`}>Updating {label(action)} {action.action_type}: Not planned</span></>}Unplanned
        </button></span></Tooltip>
        {[1, 2, 3].map((box) => {
          const active = selected && action.box_number === box;
          const isPendingChoice = busy && pendingDecision === String(box);
          const disabled = busy || !enabled || !feasibleBoxes.has(box);
          const reason = !enabled ? `Blocked: ${blockedReason}` : !feasibleBoxes.has(box) ? "Outside ETA window" : isPendingChoice ? `Updating Box ${box}` : `Box ${box}`;
          return <Tooltip key={box} title={reason}><span><button type="button" className={`${active ? "active" : ""} ${isPendingChoice ? "pending" : ""}`} aria-label={`${label(action)} ${action.action_type} choose Box ${box}`} aria-pressed={active} aria-disabled={disabled} disabled={disabled} onClick={() => onChange(action, String(box))}>
            {isPendingChoice && <><LoaderCircle size={12} className="spin" aria-hidden="true" /><span className="la-sr-only" role="status" aria-label={`Updating ${label(action)} ${action.action_type}: Box ${box}`}>Updating {label(action)} {action.action_type}: Box {box}</span></>}{box}
          </button></span></Tooltip>;
        })}
      </div>
    </div>
    {!enabled && <p className="la-blocked-reason">{blockedReason}</p>}
    {compact && <MoreInformation action={action} timezone={timezone} dependencies={pathDependencies} blockedReason={blockedReason} />}
    <Dialog open={pathOpen} onClose={() => setPathOpen(false)} PaperProps={{ className: "jecon-dialog la-dependency-dialog" }} aria-labelledby={`dependency-path-${action.id}`}>
      <DialogTitle id={`dependency-path-${action.id}`}><GitFork size={18} /> Dependency path</DialogTitle>
      <DialogContent>
        <p className="la-dependency-dialog-intro"><strong>{label(action)} {action.action_type}</strong> {enabled ? "has the following dependency relationship." : "can be planned after these required predecessor events."}</p>
        <DependencyGraph action={action} dependencies={pathDependencies} actions={allActions} timezone={timezone} onJump={(dependency) => { setPathOpen(false); onJumpToDependency(dependency); }} />
        <div className="la-dependency-path-list">{pathDependencies.map((dependency, index) => <div className="la-dependency-path-row" key={`${dependency.dependency_id || index}-${dependency.depends_on_process_id}`}>
          <div><strong>{dependencyEventLabel(dependency)}</strong><span>{String(dependency.relation || "FS").toUpperCase()}{dependency.lag_value ? ` ${Number(dependency.lag_value) > 0 ? "+" : ""}${dependency.lag_value} ${formatLagUnit(dependency.lag_unit)}` : ""} → {action.action_type}</span></div>
          <button type="button" onClick={() => { setPathOpen(false); onJumpToDependency(dependency); }}>Jump to action</button>
        </div>)}</div>
      </DialogContent>
      <DialogActions><MuiButton onClick={() => setPathOpen(false)}>Close</MuiButton></DialogActions>
    </Dialog>
  </article>;
};

const groupCandidateActions = (actions) => {
  const groups = new Map();
  actions.forEach((action) => {
    const equipmentNumber = String(action.equipment_number || "").trim();
    const key = equipmentNumber ? `equipment:${equipmentNumber}` : "equipment:unassigned";
    if (!groups.has(key)) groups.set(key, { key, equipmentNumber, actions: [] });
    groups.get(key).actions.push(action);
  });
  return [...groups.values()].map((group) => ({
    ...group,
    actions: [...group.actions].sort((left, right) => (left.action_type === "START" ? -1 : 1) - (right.action_type === "START" ? -1 : 1)),
  })).sort((left, right) => {
    if (!left.equipmentNumber) return 1;
    if (!right.equipmentNumber) return -1;
    return left.equipmentNumber.localeCompare(right.equipmentNumber, undefined, { numeric: true, sensitivity: "base" });
  });
};
const isActionOutsidePlanWindow = (action, windowStart, windowEnd) => {
  const eta = action.action_type === "END" ? action.finish_eta : action.start_eta;
  return Boolean(eta && windowStart && windowEnd && (new Date(eta) < new Date(windowStart) || new Date(eta) > new Date(windowEnd)));
};

const ProcessCandidateGroup = ({ group, timezone, windowStart, windowEnd, showOutOfWindow, busyKey, pendingDecisionByAction, onChange, onFocusAction, onJumpToDependency, allActions }) => {
  const primary = group.actions.find((action) => action.action_type === "START") || group.actions[0];
  const start = group.actions.find((action) => action.action_type === "START");
  const end = group.actions.find((action) => action.action_type === "END");
  const duration = formatDuration(end?.duration_hours ?? start?.duration_hours);
  const [expanded, setExpanded] = useState(false);
  return <section className="la-process-group">
    <header><button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}><div><div className="la-equipment-line"><strong>{group.equipmentNumber || "Unassigned equipment"}</strong>{primary.equipment_group && <Tooltip title="Equipment group"><span className="la-equipment-group-chip">{primary.equipment_group}</span></Tooltip>}</div></div><span className="la-group-department">{primary.responsible_departments?.map((item) => item.department_name).filter(Boolean).join(", ")}</span><small>{group.actions.length}</small><ChevronRight size={14} className={expanded ? "open" : ""} /></button></header>
    {expanded && <>
    {start && end && duration && <div className="la-process-timing"><span>START</span><i aria-hidden="true" /><strong>{duration}</strong><i aria-hidden="true" /><span>EXPECTED END</span></div>}
    <div className="la-process-events">{group.actions.map((action) => <Candidate key={action.id} compact showCompactDuration={!(start && end)} action={action} timezone={timezone} windowStart={windowStart} windowEnd={windowEnd} showOutOfWindow={showOutOfWindow} busy={busyKey === action.id || Boolean(pendingDecisionByAction[action.id])} pendingDecision={pendingDecisionByAction[action.id]} onChange={onChange} onFocusAction={onFocusAction} onJumpToDependency={onJumpToDependency} allActions={allActions} />)}</div></>}
  </section>;
};

const UpcomingCandidate = ({ action, timezone, onJumpToDependency }) => {
  const earliest = earliestEligibleTime(action);
  const dependency = dependencyCodes(action).join(", ");
  const constrained = constrainedDependencies(action);
  return <article className="la-upcoming-candidate">
    <div><span className={`la-kind ${action.action_type.toLowerCase()}`}>{action.action_type}</span><strong>{label(action)}</strong></div>
    <p>{action.process_name || "Unnamed process"}</p>
    {dependency && <small>Waiting on: {dependency}</small>}
    <em>{earliest ? `Eligible from ${formatPlanDateTime(earliest, timezone)}` : "Waiting for its predecessor to be completed and validated."}</em>
    {!!constrained.length && <button type="button" onClick={() => onJumpToDependency(constrained[0])} aria-label={`View predecessor for ${label(action)} ${action.action_type}`}>View predecessor</button>}
  </article>;
};

const BoxAction = ({ action, timezone, editable, busy, onChange }) => {
  const [removeOpen, setRemoveOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const hasDependents = Boolean(action.downstream_processes?.length);
  const remove = () => {
    if (hasDependents) setRemoveOpen(true);
    else onChange(action, "NO");
  };
  return <article id={`la-box-action-${action.id}`} className="la-box-action planned">
    <div className="la-box-action-head"><span className={`la-kind ${action.action_type.toLowerCase()}`}>{action.action_type}</span><span className="la-box-action-code">{action.process_code || label(action)}</span><span className={`la-outcome ${(action.validation_outcome || "valid").toLowerCase()}`}>{action.validation_outcome || "VALID"}</span></div>
    <strong className="la-box-action-name">{action.process_name || "Unnamed process"}</strong>
    <div className="la-box-context">{action.equipment_number && <span className="la-box-equipment">Equipment: {action.equipment_number}</span>}{action.equipment_group && <span>{action.equipment_group}</span>}{!!action.responsible_departments?.length && <span>{action.responsible_departments.map((item) => item.department_name).filter(Boolean).join(", ")}</span>}<ActionTiming action={action} timezone={timezone} compact /></div>
    <Dependencies action={action} />
    <EndDuration action={action} />
    {editable ? <div className="la-box-controls no-print"><div className="la-move-menu"><button type="button" className="la-move-action" aria-label="Move to another box" aria-expanded={moveOpen} disabled={busy} onClick={() => setMoveOpen((value) => !value)}><MoreHorizontal size={15} /></button>{moveOpen && <div role="menu">{(action.feasible_boxes || []).filter((box) => box !== action.box_number).map((box) => <button type="button" role="menuitem" key={box} onClick={() => { setMoveOpen(false); onChange(action, String(box)); }}>Box {box}</button>)}{!(action.feasible_boxes || []).some((box) => box !== action.box_number) && <span>No other boxes</span>}</div>}</div><button type="button" className="la-remove-action" aria-label={`Remove ${label(action)} from Box ${action.box_number}`} disabled={busy} onClick={remove}>{busy ? <><LoaderCircle size={12} className="spin" aria-hidden="true" /><span>Removing…</span><span className="la-sr-only" role="status">Removing {label(action)} from Box {action.box_number}</span></> : <><Trash2 size={12} /> Remove</>}</button></div> : null}
    <Dialog open={removeOpen} onClose={() => setRemoveOpen(false)} PaperProps={{ className: "jecon-dialog la-confirm-dialog la-remove-dialog" }}><DialogTitle>Remove planned action?</DialogTitle><DialogContent><div className="la-confirm-icon"><Trash2 size={22} /></div><p>This action may affect {action.downstream_processes.length} dependent action{action.downstream_processes.length === 1 ? "" : "s"}. Remove it from this box?</p></DialogContent><DialogActions><MuiButton disabled={busy} onClick={() => setRemoveOpen(false)}>Cancel</MuiButton><MuiButton color="error" variant="contained" disabled={busy} onClick={() => { setRemoveOpen(false); onChange(action, "NO"); }}>Remove action</MuiButton></DialogActions></Dialog>
  </article>;
};

const boxActions = (plan, number) => (plan.actions || []).filter((item) => item.decision === "YES" && item.box_number === number);
const boxDuration = (actions) => actions.reduce((total, action) => total + (Number.isFinite(Number(action.duration_hours)) ? Number(action.duration_hours) : 0), 0);
const formatBoxHours = (hours) => hours ? formatDuration(hours) : "0 hrs";
const BOX_ACTION_PREVIEW_COUNT = 6;

const PlanBox = ({ box, actions, plan, editable, busyKey, pendingDecisionByAction, onChange, focusedActionId }) => {
  const focusedAction = actions.find((action) => action.id === focusedActionId);
  const [expanded, setExpanded] = useState(false);
  const hasHiddenFocusedAction = Boolean(focusedAction && !actions.slice(0, BOX_ACTION_PREVIEW_COUNT).some((action) => action.id === focusedAction.id));
  const showAllActions = expanded || hasHiddenFocusedAction;
  const visibleActions = showAllActions ? actions : actions.slice(0, BOX_ACTION_PREVIEW_COUNT);
  const hiddenCount = Math.max(0, actions.length - BOX_ACTION_PREVIEW_COUNT);

  return <article className={`la-time-box ${focusedAction ? "focused" : ""}`}>
    <header><div><span><strong>Box {box.number}</strong></span><p>{formatBoxWindow(box, plan.timezone)}</p><small>{actions.length} action{actions.length === 1 ? "" : "s"} · {formatBoxHours(boxDuration(actions))} work</small></div></header>
    <div className="la-box-list">
      {visibleActions.map((action) => <BoxAction key={action.id} action={action} timezone={plan.timezone} editable={editable} busy={busyKey === action.id || Boolean(pendingDecisionByAction[action.id])} onChange={onChange} />)}
      {!!hiddenCount && <button type="button" className="la-show-more-actions" onClick={() => setExpanded((value) => !value)} aria-expanded={showAllActions}>{showAllActions ? "Show fewer actions ↑" : `Show ${hiddenCount} more action${hiddenCount === 1 ? "" : "s"} ↓`}</button>}
      {!actions.length && <div className="la-box-empty"><CalendarClock size={20} /><strong>No actions assigned</strong><span>Choose an action from Plan actions to place it in this box.</span></div>}
    </div>
  </article>;
};

/* Retained implementation replaced by PlanBox below.
  {(plan.boxes || []).map((box) => {
    const actions = boxActions(plan, box.number);
    const focusedAction = actions.find((action) => action.id === focusedActionId);
    return <article className={`la-time-box ${focusedAction ? "focused" : ""}`} key={box.number}>
      <header><div><span><strong>Box {box.number}</strong> · {formatBoxWindow(box, plan.timezone)}</span><small>{actions.length} action{actions.length === 1 ? "" : "s"} · {formatBoxHours(boxDuration(actions))} work</small></div></header>
      <div className="la-box-list">{actions.map((action) => <BoxAction key={action.id} action={action} timezone={plan.timezone} editable={editable} busy={busyKey === action.id || Boolean(pendingDecisionByAction[action.id])} onChange={onChange} onInstruction={onInstruction} />)}
        {!actions.length && <div className="la-box-empty"><CalendarClock size={20} /><strong>No actions assigned</strong><span>Choose an action from Plan actions to place it in this box.</span></div>}
      </div>
    </article>;
  })}
</section>;
*/

const PlanBoxes = ({ plan, editable = false, busyKey, pendingDecisionByAction = {}, onChange, focusedActionId }) => {
  return <section className="la-box-grid" aria-label="36-hour planning boxes">
    {plan.boxes.map((box) => <PlanBox key={box.number} box={box} actions={boxActions(plan, box.number)} plan={plan} editable={editable} busyKey={busyKey} pendingDecisionByAction={pendingDecisionByAction} onChange={onChange} focusedActionId={focusedActionId} />)}
  </section>;
};

const Validation = ({ plan }) => {
  const validation = plan.validation || {};
  const blocking = validation.blocking_errors || [];
  const warnings = validation.warnings || [];
  const dataIssues = plan.data_issues || [];
  const dataIssueCodes = new Set(dataIssues.flatMap((item) => (item.errors || []).map((issue) => issue.code)));
  const otherBlocking = blocking.filter((item) => !dataIssueCodes.has(item.code));
  if (!blocking.length && !warnings.length && !dataIssues.length) return null;
  return <details className={`la-validation ${blocking.length || dataIssues.length ? "blocked" : ""}`} open={blocking.length > 0 || dataIssues.length > 0}>
    <summary><ShieldAlert size={16} /> Validation details <span>{validation.blocking_error_count ?? blocking.length} blocking · {warnings.length} warnings</span></summary>
    <div>{otherBlocking.map((item, index) => <p className="is-error" key={`${item.code}-${index}`}><XCircle size={14} /><span><strong>{item.code}</strong>{item.message}</span></p>)}
      {warnings.map((item, index) => <p className="is-warning" key={`${item.code}-${index}`}><AlertCircle size={14} /><span><strong>{item.code}</strong>{item.message}</span></p>)}
      {dataIssues.map((item, index) => <p className="is-error" key={`${item.equipment_process_id}-${index}`}><XCircle size={14} /><span><strong>{item.process_code || "DATA ISSUE"}</strong>{item.errors?.[0]?.message || "Scheduling data is invalid."}</span></p>)}</div>
  </details>;
};

const PublishedPlan = ({ plan }) => <div className="la-published-sheet">
  <div className="la-print-heading"><div><span>JECON 36-HOUR PRODUCTION PLAN</span><h3>{plan.plan_number || "Published Look Ahead"}</h3></div><div><strong>{plan.status}</strong><span>{plan.timezone}</span></div></div>
  <div className="la-baseline-meta"><span><strong>Window</strong>{formatPlanDateTime(plan.window_start, plan.timezone)} — {formatPlanDateTime(plan.window_end, plan.timezone)}</span><span><strong>Published</strong>{formatPlanDateTime(plan.published_at, plan.timezone)}</span><span><strong>Publisher</strong>{plan.published_by || "—"}</span></div>
  {plan.notes && <p className="la-baseline-notes"><strong>Plan notes:</strong> {plan.notes}</p>}
  <PlanBoxes plan={plan} />
</div>;

const Empty = ({ icon: Icon, title, text, children }) => <div className="la-empty"><span><Icon size={24} /></span><strong>{title}</strong><p>{text}</p>{children}</div>;

const NotesEditor = React.memo(({ planNotes, busy, onSave }) => {
  const [value, setValue] = useState(planNotes || "");
  useEffect(() => setValue(planNotes || ""), [planNotes]);
  const changed = value.trim() !== (planNotes || "").trim();
  return <section className="la-notes no-print" aria-label="Production notes"><header><div><strong>Production notes</strong><span>Global instructions and special notes for the planning team</span></div>{changed && <em aria-label="Unsaved changes" title="Unsaved changes" />}</header><div className="la-notes-editor"><textarea value={value} disabled={busy} placeholder="Add global plan-level notes or instructions" onChange={(event) => setValue(event.target.value)} /><button type="button" className="la-button secondary" disabled={busy || !changed} onClick={() => onSave(value)}><Save size={15} /> {busy ? "Saving…" : "Save notes"}</button></div></section>;
});

const LookAheadSkeleton = () => <div className="la-skeleton" role="status" aria-label="Loading Look Ahead">
  <span className="la-sr-only">Loading Look Ahead…</span>
  <section className="la-skeleton-strip">
    {["wide", "short", "short", "short"].map((width, index) => <div key={index}><span className="la-skeleton-line label" /><span className={`la-skeleton-line ${width}`} /><span className="la-skeleton-line meta" /></div>)}
  </section>
  <section className="la-skeleton-layout">
    <aside className="la-skeleton-panel">
      <div className="la-skeleton-panel-head"><span className="la-skeleton-line title" /><span className="la-skeleton-input" /></div>
      <div className="la-skeleton-list">{[0, 1, 2, 3].map((item) => <div className="la-skeleton-card" key={item}><span className="la-skeleton-line medium" /><span className="la-skeleton-line wide" /><span className="la-skeleton-line meta" /></div>)}</div>
    </aside>
    <div className="la-skeleton-boxes">{[0, 1, 2].map((item) => <div className="la-skeleton-box" key={item}><div><span className="la-skeleton-line label" /><span className="la-skeleton-line medium" /></div><span className="la-skeleton-line wide" /><div className="la-skeleton-card"><span className="la-skeleton-line medium" /><span className="la-skeleton-line wide" /><span className="la-skeleton-line meta" /></div></div>)}</div>
  </section>
</div>;

function LookAhead({ isSidebarExpanded }) {
  const [projectId, setProjectId] = useState(readSelectedProjectId);
  const [mode, setMode] = useState(() => roleMayPrepare() ? "draft" : "current");
  const [canPrepare, setCanPrepare] = useState(roleMayPrepare);
  const [plan, setPlan] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [pendingDecisionByAction, setPendingDecisionByAction] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [candidateTab, setCandidateTab] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [equipmentGroupFilter, setEquipmentGroupFilter] = useState("");
  const [candidateSort, setCandidateSort] = useState("process_code");
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showOutOfWindow, setShowOutOfWindow] = useState(true);
  const [focusedActionId, setFocusedActionId] = useState(null);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [selectedBox, setSelectedBox] = useState("all");
  const sequence = useRef(0);
  const pendingDecisionRef = useRef(new Map());

  const replacePlan = useCallback((next) => { setPlan(next || null); }, []);
  const mergeActionDelta = useCallback((delta) => {
    setPlan((current) => mergeLookAheadActionDelta(current, delta));
  }, []);
  const load = useCallback(async (signal) => {
    if (!projectId) { replacePlan(null); setHistoryRows([]); setLoading(false); return; }
    const requestId = ++sequence.current;
    setLoading(true); setError("");
    try {
      if (mode === "draft") {
        const draft = await getLookAheadDraft(projectId, signal);
        if (requestId !== sequence.current) return;
        setCanPrepare(true); replacePlan(draft);
      } else if (mode === "current") {
        const current = await getCurrentLookAheadPlan(projectId, signal);
        if (requestId !== sequence.current) return;
        replacePlan(current);
      } else {
        const result = await getLookAheadHistory(projectId, { page: historyPage, pageSize: PAGE_SIZE }, signal);
        if (requestId !== sequence.current) return;
        setHistoryRows(result.plans); setHistoryTotal(result.pagination.total || 0);
      }
    } catch (requestError) {
      if (requestError?.code === "ERR_CANCELED" || signal?.aborted || requestId !== sequence.current) return;
      const parsed = getLookAheadError(requestError);
      if (parsed.status === 404) { if (mode === "history") { setHistoryRows([]); setHistoryTotal(0); } else replacePlan(null); }
      else if (mode === "draft" && parsed.status === 403) { setCanPrepare(false); setMode("current"); }
      else { setNotice(""); setError(parsed.message); }
    } finally { if (requestId === sequence.current) setLoading(false); }
  }, [historyPage, mode, projectId, replacePlan]);

  useEffect(() => { const controller = new AbortController(); load(controller.signal); return () => controller.abort(); }, [load]);
  useEffect(() => {
    const change = (event) => {
      const next = String(event?.detail?.project_id || readSelectedProjectId() || "").trim();
      setProjectId((current) => current === next ? current : next);
      setHistoryPage(1); setHistoryDetail(null); setNotice(""); setQuery(""); setCandidateTab("all"); setDepartmentFilter(""); setEquipmentGroupFilter(""); setShowUpcoming(false); setFocusedActionId(null);
    };
    window.addEventListener("projectChanged", change); window.addEventListener("storage", change);
    return () => { window.removeEventListener("projectChanged", change); window.removeEventListener("storage", change); };
  }, []);

  const mutationError = useCallback(async (requestError) => {
    const parsed = getLookAheadError(requestError);
    setNotice("");
    if (parsed.code === "STALE_DRAFT_VERSION") {
      try { replacePlan(await getLookAheadDraft(projectId)); setNotice("Draft changed by another planner. Latest version loaded."); }
      catch (reloadError) { setError(getLookAheadError(reloadError).message); }
    } else setError(parsed.message);
  }, [projectId, replacePlan]);

  const generate = async () => {
    setBusyKey("generate"); setError(""); setNotice("");
    try { const result = await createLookAheadDraft(projectId); setCanPrepare(true); replacePlan(result.plan); setMode("draft"); setNotice(result.created ? "New 36-hour draft generated." : "Existing active draft loaded."); }
    catch (requestError) { const parsed = getLookAheadError(requestError); if (parsed.status === 403) setCanPrepare(false); setError(parsed.message); }
    finally { setBusyKey(""); }
  };
  const changeAction = async (action, value) => {
    // The ref closes the small gap before React renders disabled controls, so a
    // rapid double click can never start a second PATCH for this action.
    if (pendingDecisionRef.current.has(action.id)) return;
    pendingDecisionRef.current.set(action.id, value);
    setPendingDecisionByAction((current) => ({ ...current, [action.id]: value }));
    setError(""); setNotice("");
    const payload = value === "NO" ? { version: plan.version, decision: "NO", box_number: null } : { version: plan.version, decision: "YES", box_number: Number(value) };
    try {
      const delta = await updateLookAheadAction(projectId, plan.id, action.id, payload);
      mergeActionDelta(delta);
      if (value !== "NO") setFocusedActionId(action.id);
      if (action.action_type === "START" && value !== "NO") {
        const derivedEnd = (delta.actions || []).find((item) => (
          item.equipment_process_id === action.equipment_process_id
          && item.action_type === "END"
          && item.decision === "YES"
        ));
        if (derivedEnd) {
          const duration = formatDuration(derivedEnd.duration_hours ?? action.duration_hours);
          const durationText = duration ? `, expected ${duration} after START` : "";
          setNotice(`END was automatically placed in Box ${derivedEnd.box_number}${durationText}.`);
        }
      }
    }
    catch (requestError) { await mutationError(requestError); }
    finally {
      pendingDecisionRef.current.delete(action.id);
      setPendingDecisionByAction((current) => {
        const { [action.id]: _completedDecision, ...remaining } = current;
        return remaining;
      });
    }
  };
  const saveNotes = async (notes) => {
    setBusyKey("notes"); setError("");
    try { const saved = await updateLookAheadNotes(projectId, plan.id, { version: plan.version, notes }); replacePlan({ ...plan, ...saved }); setNotice("Plan notes saved."); }
    catch (requestError) { await mutationError(requestError); }
    finally { setBusyKey(""); }
  };
  const validate = async () => {
    setBusyKey("validate"); setError("");
    try { const result = await validateLookAheadDraft(projectId, plan.id, plan.version); replacePlan(result.data); setNotice(result.validation?.can_publish ? "Validation passed. Draft is ready to publish." : "Validation completed with blocking issues."); }
    catch (requestError) { await mutationError(requestError); }
    finally { setBusyKey(""); }
  };
  const regenerate = async () => {
    setConfirmAction(null);
    setBusyKey("regenerate"); setError("");
    try { replacePlan(await regenerateLookAheadDraft(projectId, plan.id, plan.version)); setNotice("Draft regenerated from latest source data."); }
    catch (requestError) { await mutationError(requestError); }
    finally { setBusyKey(""); }
  };
  const publish = async () => {
    setConfirmAction(null);
    setBusyKey("publish"); setError("");
    try { replacePlan(await publishLookAheadDraft(projectId, plan.id, plan.version)); setMode("current"); setNotice("Look Ahead plan published successfully."); }
    catch (requestError) { await mutationError(requestError); }
    finally { setBusyKey(""); }
  };
  const openHistory = async (id) => {
    setBusyKey(`history-${id}`); setError("");
    try { setHistoryDetail(await getLookAheadPlan(projectId, id)); }
    catch (requestError) { setError(getLookAheadError(requestError).message); }
    finally { setBusyKey(""); }
  };
  const printPublishedPlan = async (box = "all", format = "pdf") => {
    const printablePlan = historyDetail || plan;
    if (!printablePlan?.id) return;
    setBusyKey("print"); setError("");
    try {
      if (format === "pdf") {
        await downloadLookAheadPlanPdf(projectId, printablePlan.id, box);
      } else {
        await downloadLookAheadPlan(projectId, printablePlan.id, format, box);
      }
      setPrintDialogOpen(false);
    }
    catch (requestError) { setError(getLookAheadError(requestError).message); }
    finally { setBusyKey(""); }
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (plan?.actions || []).filter((item) => (
      (!needle || searchText(item).includes(needle))
      && (!departmentFilter || (item.responsible_departments || []).some((department) => department.department_name === departmentFilter))
      && (!equipmentGroupFilter || item.equipment_group === equipmentGroupFilter)
    ));
  }, [departmentFilter, equipmentGroupFilter, plan?.actions, query]);
  const filterOptions = useMemo(() => {
    const actions = plan?.actions || [];
    return {
      departments: [...new Set(actions.flatMap((item) => (item.responsible_departments || []).map((department) => department.department_name)).filter(Boolean))].sort(),
      groups: [...new Set(actions.map((item) => item.equipment_group).filter(Boolean))].sort(),
    };
  }, [plan?.actions]);
  const jumpToDependency = useCallback((dependency) => {
    const predecessorId = String(dependency?.depends_on_process_id || "");
    const predecessorEvent = String(dependency?.required_predecessor_event || "").toUpperCase();
    const target = (plan?.actions || []).find((item) => (
      String(item.equipment_process_id || "") === predecessorId
      && String(item.action_type || "").toUpperCase() === predecessorEvent
    ));
    if (!target) {
      setNotice("The required predecessor is not available in this planning window.");
      return;
    }
    setQuery(target.process_code || target.equipment_number || "");
    setCandidateTab(target.source_type === "MILESTONE" ? "milestones" : "processes");
    window.setTimeout(() => {
      const element = document.getElementById(`la-action-${target.id}`);
      if (!element) return;
      element.classList.add("la-jump-highlight");
      if (typeof element.scrollIntoView === "function") element.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => element.classList.remove("la-jump-highlight"), 2200);
    }, 50);
  }, [plan?.actions]);
  const focusPlanAction = useCallback((action) => {
    setFocusedActionId(action.id);
    window.setTimeout(() => document.getElementById(`la-box-action-${action.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  }, []);
  const sortedCandidates = useMemo(
    () => [...filtered].sort((left, right) => compareCandidateActions(left, right, candidateSort)),
    [candidateSort, filtered],
  );
  const primaryProcessIds = new Set(sortedCandidates
    .filter((item) => item.selectable || item.decision === "YES")
    .map((item) => String(item.equipment_process_id || "")));
  const allVisibleCandidates = sortedCandidates.filter((item) => (
    item.selectable
    || item.decision === "YES"
    || (item.action_type === "END" && primaryProcessIds.has(String(item.equipment_process_id || "")))
  ));
  const visibleCandidates = candidateTab === "milestones"
    ? allVisibleCandidates.filter((item) => item.source_type === "MILESTONE")
    : candidateTab === "processes"
      ? allVisibleCandidates.filter((item) => item.source_type !== "MILESTONE")
      : allVisibleCandidates;
  const candidateScope = candidateTab === "milestones"
    ? sortedCandidates.filter((item) => item.source_type === "MILESTONE")
    : candidateTab === "processes"
      ? sortedCandidates.filter((item) => item.source_type !== "MILESTONE")
      : sortedCandidates;
  const visibleCandidateIds = new Set(visibleCandidates.map((item) => item.id));
  const outsideWindowCount = visibleCandidates.filter((item) => isActionOutsidePlanWindow(item, plan?.window_start, plan?.window_end)).length;
  const upcomingCandidates = candidateScope.filter((item) => !visibleCandidateIds.has(item.id) && isUpcomingDependency(item));
  const milestoneCount = allVisibleCandidates.filter((item) => item.source_type === "MILESTONE").length;
  const processCount = allVisibleCandidates.filter((item) => item.source_type !== "MILESTONE").length;
  const candidateGroups = useMemo(() => groupCandidateActions(visibleCandidates), [visibleCandidates]);
  const selected = (plan?.actions || []).filter((item) => item.decision === "YES").length;
  const validation = plan?.validation || {};
  const pages = Math.max(1, Math.ceil(historyTotal / PAGE_SIZE));
  const busy = Boolean(busyKey);

  return <div className={`table-container lookahead-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}><div className="lookahead-content">
    <header className="la-page-header no-print"><div><h2>36-Hour Look Ahead</h2><p>Equipment-process and milestone production planning</p></div><div className="la-header-actions">
      <Tooltip title="Refresh data"><span><button type="button" className="la-button secondary icon" aria-label="Refresh data" disabled={loading || busy || !projectId} onClick={() => load()}><RefreshCcw size={17} className={loading ? "spin" : ""} /></button></span></Tooltip>
      {((mode === "current" && plan) || historyDetail) && <button type="button" className="la-button secondary" disabled={busy} onClick={() => setPrintDialogOpen(true)}><Printer size={16} /> Print</button>}
      {mode === "draft" && plan && <><button type="button" className="la-button secondary" disabled={busy} onClick={validate}><CheckCircle2 size={16} /> Validate</button><button type="button" className="la-button secondary" disabled={busy} onClick={() => setConfirmAction("regenerate")}><RotateCcw size={16} /> Regenerate</button><button type="button" className="la-button primary" disabled={busy || validation.can_publish === false} onClick={() => setConfirmAction("publish")}><Send size={16} /> Publish</button></>}
      {!plan && mode !== "history" && canPrepare && projectId && !loading && <button type="button" className="la-button primary" disabled={busy} aria-busy={busyKey === "generate"} onClick={generate}>{busyKey === "generate" ? <LoaderCircle size={16} className="spin" /> : <CalendarClock size={16} />} {busyKey === "generate" ? "Generating draft…" : "Generate draft"}</button>}
    </div></header>
    <nav className="la-tabs no-print" role="tablist">{[["draft", "Planning Draft", CalendarClock], ["current", "Published Plan", FileClock], ["history", "History", History]].map(([value, text, Icon]) => <button key={value} type="button" role="tab" aria-selected={mode === value} className={mode === value ? "active" : ""} onClick={() => { setMode(value); setHistoryDetail(null); setError(""); setNotice(""); }}><Icon size={15} /> {text}</button>)}</nav>
    <main className={`la-main ${mode === "draft" && plan ? "draft-mode" : ""}`}>
      {!projectId && <Empty icon={Flag} title="Select a project" text="Choose a project from the sidebar to load its 36-hour Look Ahead plan." />}
      {projectId && loading && <LookAheadSkeleton />}
      {projectId && !loading && mode === "draft" && !plan && <Empty icon={CalendarClock} title="No active planning draft" text={canPrepare ? "Generate a fixed 36-hour window and start selecting process and milestone actions." : "You can view published plans, but your role cannot prepare a draft."}>{canPrepare && <button type="button" className="la-button primary" disabled={busy} aria-busy={busyKey === "generate"} onClick={generate}>{busyKey === "generate" ? <LoaderCircle size={16} className="spin" /> : <CalendarClock size={16} />} {busyKey === "generate" ? "Generating 36-hour draft…" : "Generate 36-hour draft"}</button>}</Empty>}
      {projectId && !loading && mode === "draft" && plan && <>
        <section className="la-plan-strip" aria-label="Planning summary"><strong className="la-window-summary">{formatPlanDateTime(plan.window_start, plan.timezone)} → {formatPlanDateTime(plan.window_end, plan.timezone)} · {plan.timezone} · v{plan.version}</strong><span><b>{selected}</b> selected actions</span><span className={validation.blocking_error_count ? "danger" : ""}><b>{validation.blocking_error_count || 0}</b> blocking errors</span><span><b>{validation.warning_count || 0}</b> warnings</span></section>
        <Validation plan={plan} />
        <section className="la-draft-layout"><aside className="la-candidates no-print"><div className="la-panel-head"><div><h3>Plan actions</h3><p>Available actions and actions already placed in a box</p></div><div className="la-candidate-tabs" role="tablist" aria-label="Candidate action type"><button type="button" role="tab" aria-selected={candidateTab === "all"} className={candidateTab === "all" ? "active" : ""} onClick={() => setCandidateTab("all")}>All <span>{allVisibleCandidates.length}</span></button><button type="button" role="tab" aria-selected={candidateTab === "processes"} className={candidateTab === "processes" ? "active" : ""} onClick={() => setCandidateTab("processes")}>Processes <span>{processCount}</span></button><button type="button" role="tab" aria-selected={candidateTab === "milestones"} className={candidateTab === "milestones" ? "active" : ""} onClick={() => setCandidateTab("milestones")}>Milestones <span>{milestoneCount}</span></button></div><div className="la-filter-row"><label className="la-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search process or equipment" /></label><select aria-label="Filter by department" value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)}><option value="">All departments</option>{filterOptions.departments.map((department) => <option key={department} value={department}>{department}</option>)}</select><select aria-label="Filter by equipment group" value={equipmentGroupFilter} onChange={(event) => setEquipmentGroupFilter(event.target.value)}><option value="">All equipment groups</option>{filterOptions.groups.map((group) => <option key={group} value={group}>{group}</option>)}</select><select aria-label="Sort candidates" value={candidateSort} onChange={(event) => setCandidateSort(event.target.value)}><option value="process_code">Sort: Process code</option><option value="equipment">Sort: Equipment number</option></select></div>{(query || departmentFilter || equipmentGroupFilter) && <div className="la-filter-chips" aria-label="Active filters">{query && <span>Search: {query}</span>}{departmentFilter && <span>Department: {departmentFilter}</span>}{equipmentGroupFilter && <span>Equipment: {equipmentGroupFilter}</span>}<button type="button" onClick={() => { setQuery(""); setDepartmentFilter(""); setEquipmentGroupFilter(""); }}>Clear</button></div>}</div><div className="la-candidate-scroll">
          {!!visibleCandidates.length && <section className="la-candidate-group"><h4>{candidateTab === "milestones" ? <><Milestone size={15} /> Milestones / Dependency Events</> : candidateTab === "processes" ? <><Flag size={15} /> Equipment Processes</> : <><CalendarClock size={15} /> All candidate actions</>} <span>{visibleCandidates.length}</span></h4>{candidateGroups.map((group) => <ProcessCandidateGroup key={group.key} group={group} timezone={plan.timezone} windowStart={plan.window_start} windowEnd={plan.window_end} showOutOfWindow={showOutOfWindow} busyKey={busyKey} pendingDecisionByAction={pendingDecisionByAction} onChange={changeAction} onFocusAction={focusPlanAction} onJumpToDependency={jumpToDependency} allActions={plan.actions} />)}</section>}
          {!visibleCandidates.length && <p className="la-no-results">No {candidateTab === "all" ? "actions" : candidateTab} match your search.</p>}
          {!!upcomingCandidates.length && <section className="la-upcoming"><button type="button" aria-expanded={showUpcoming} onClick={() => setShowUpcoming((shown) => !shown)}><CalendarClock size={14} /> Upcoming dependencies <span>{upcomingCandidates.length}</span><ChevronRight size={14} className={showUpcoming ? "open" : ""} /></button>{showUpcoming && <><p>Not schedulable in this 36-hour window. They will become actionable once their constraints are met.</p>{upcomingCandidates.slice(0, 100).map((action) => <UpcomingCandidate key={action.id} action={action} timezone={plan.timezone} onJumpToDependency={jumpToDependency} />)}{upcomingCandidates.length > 100 && <small className="la-upcoming-limit">Showing the first 100 results. Refine your search or filters to narrow this list.</small>}</>}</section>}
        </div></aside><div className="la-plan-area"><NotesEditor planNotes={plan.notes} busy={busyKey === "notes"} onSave={saveNotes} /><PlanBoxes plan={plan} editable busyKey={busyKey} pendingDecisionByAction={pendingDecisionByAction} onChange={changeAction} focusedActionId={focusedActionId} /></div></section>
      </>}
      {projectId && !loading && mode === "current" && !plan && <Empty icon={FileClock} title="No published plan" text="This project does not have a current published 36-hour baseline yet.">{canPrepare && <button type="button" className="la-button primary" onClick={generate}><CalendarClock size={16} /> Prepare first draft</button>}</Empty>}
      {projectId && !loading && mode === "current" && plan && <PublishedPlan plan={plan} />}
      {projectId && !loading && mode === "history" && (historyDetail ? <div className="la-history-detail"><button type="button" className="la-back no-print" onClick={() => setHistoryDetail(null)}><ChevronLeft size={16} /> Back to history</button><PublishedPlan plan={historyDetail} /></div> : <section className="la-history-card"><div className="la-history-scroll"><table><thead><tr><th>Plan</th><th>Status</th><th>Planning window</th><th>Published</th><th>Publisher</th><th aria-label="Actions" /></tr></thead><tbody>{historyRows.map((row) => <tr key={row.id}><td><strong>{row.plan_number || row.id}</strong></td><td><span className={`la-plan-status ${row.status.toLowerCase()}`}>{row.status}</span></td><td>{formatPlanDateTime(row.window_start, row.timezone)}<small>to {formatPlanDateTime(row.window_end, row.timezone)}</small></td><td>{formatPlanDateTime(row.published_at, row.timezone)}</td><td>{row.published_by || "—"}</td><td><button type="button" disabled={busyKey === `history-${row.id}`} onClick={() => openHistory(row.id)}>{busyKey === `history-${row.id}` ? "Loading…" : "View"}</button></td></tr>)}{!historyRows.length && <tr><td colSpan="6" className="la-history-empty">No published Look Ahead history is available.</td></tr>}</tbody></table></div><footer><span>{historyTotal} published plan{historyTotal === 1 ? "" : "s"}</span><div><button type="button" disabled={historyPage <= 1} onClick={() => setHistoryPage((value) => value - 1)}><ChevronLeft size={17} /></button><span>Page <strong>{historyPage}</strong> of {pages}</span><button type="button" disabled={historyPage >= pages} onClick={() => setHistoryPage((value) => value + 1)}><ChevronRight size={17} /></button></div></footer></section>)}
    </main>
    <Dialog open={Boolean(confirmAction)} onClose={() => !busy && setConfirmAction(null)} PaperProps={{ className: "jecon-dialog la-confirm-dialog" }}>
      <DialogTitle>{confirmAction === "publish" ? "Publish production baseline?" : "Regenerate planning draft?"}</DialogTitle>
      <DialogContent>
        <div className={`la-confirm-icon ${confirmAction || ""}`}>{confirmAction === "publish" ? <Send size={22} /> : <RotateCcw size={22} />}</div>
        <p>{confirmAction === "publish" ? "This will create an immutable baseline and supersede the project's current published plan, if one exists." : "A new 36-hour window will be generated from the latest source data. Current selections and action instructions will be replaced."}</p>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={() => setConfirmAction(null)} disabled={busy}>Cancel</MuiButton>
        <MuiButton variant="contained" color="error" disabled={busy} onClick={confirmAction === "publish" ? publish : regenerate}>{busy ? "Please wait…" : confirmAction === "publish" ? "Publish" : "Regenerate"}</MuiButton>
      </DialogActions>
    </Dialog>
    <Dialog open={printDialogOpen} onClose={() => !busy && setPrintDialogOpen(false)} PaperProps={{ className: "jecon-dialog la-print-dialog" }}>
      <DialogTitle><Printer size={18} /> Export production plan</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: "20px" }}>
          <strong>Step 1: Select format</strong>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button type="button" disabled={busy} style={{ flex: 1, padding: "12px", border: exportFormat === "pdf" ? "2px solid #1976d2" : "1px solid #ddd", borderRadius: "4px", cursor: "pointer", backgroundColor: exportFormat === "pdf" ? "#e3f2fd" : "#fff" }} onClick={() => setExportFormat("pdf")}><strong>📄 PDF</strong></button>
            <button type="button" disabled={busy} style={{ flex: 1, padding: "12px", border: exportFormat === "excel" ? "2px solid #1976d2" : "1px solid #ddd", borderRadius: "4px", cursor: "pointer", backgroundColor: exportFormat === "excel" ? "#e3f2fd" : "#fff" }} onClick={() => setExportFormat("excel")}><strong>📊 Excel</strong></button>
          </div>
        </div>
        <div>
          <strong>Step 2: Select the shift sheet to generate</strong>
          <div className="la-print-options">
            <button type="button" disabled={busy} onClick={() => setSelectedBox("all")} style={{ border: selectedBox === "all" ? "2px solid #1976d2" : "1px solid #ddd", backgroundColor: selectedBox === "all" ? "#e3f2fd" : "#fff" }}><strong>All shifts</strong><span>Complete 36-hour production plan</span></button>
            {(historyDetail || plan)?.boxes?.map((box) => <button type="button" key={box.number} disabled={busy} onClick={() => setSelectedBox(String(box.number))} style={{ border: selectedBox === String(box.number) ? "2px solid #1976d2" : "1px solid #ddd", backgroundColor: selectedBox === String(box.number) ? "#e3f2fd" : "#fff" }}><strong>Box {box.number}</strong><span>{formatPlanDateTime(box.start, (historyDetail || plan)?.timezone)} to {formatPlanDateTime(box.end, (historyDetail || plan)?.timezone)}</span></button>)}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={() => { setPrintDialogOpen(false); setExportFormat("pdf"); setSelectedBox("all"); }} disabled={busy}>Cancel</MuiButton>
        <MuiButton variant="contained" color="primary" disabled={busy} onClick={() => { printPublishedPlan(selectedBox === "all" ? "all" : selectedBox, exportFormat); setPrintDialogOpen(false); setExportFormat("pdf"); setSelectedBox("all"); }}>Export</MuiButton>
      </DialogActions>
    </Dialog>
    <Snackbar open={Boolean(error || notice)} autoHideDuration={4500} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} onClose={(_, reason) => { if (reason !== "clickaway") { setError(""); setNotice(""); } }}>
      <MuiAlert severity={error ? "error" : "success"} variant="filled" onClose={() => { setError(""); setNotice(""); }} sx={{ width: "100%" }}>{error || notice}</MuiAlert>
    </Snackbar>
  </div></div>;
}

export default LookAhead;
